// src/components/PublicationList.jsx
import React, { useState, useEffect } from "react";
import journalPublications from "../data/journalPublications";
import conferencePublications from "../data/conferencePublications";
import posterPublications from "../data/posterPublications";

const PublicationList = ({ publicationType }) => {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);

  // pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 6; // 🔹 hard-coded

  useEffect(() => {
    let dataToUse = [];
    switch (publicationType) {
      case "Journal Article":
        dataToUse = journalPublications;
        break;
      case "Conference Paper":
        dataToUse = conferencePublications;
        break;
      case "Poster":
        dataToUse = posterPublications;
        break;
      default:
        dataToUse = [];
        break;
    }

    const sorted = [...dataToUse].sort(
      (a, b) => new Date(b.publicationDate) - new Date(a.publicationDate)
    );

    setPublications(sorted);
    setLoading(false);
  }, [publicationType]);

  if (loading) {
    return <p className="text-center">Loading...</p>;
  }

  if (publications.length === 0) {
    return (
      <p className="text-center text-gray-600">
        No {publicationType?.toLowerCase()} available at the moment.
      </p>
    );
  }

  // pagination logic
  const totalPages = Math.ceil(publications.length / itemsPerPage);
  const paginated = publications.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const goToPage = (pageIndex) => setCurrentPage(pageIndex);

  return (
    <div>
      {/* Grid of publications */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {paginated.map((pub) => (
          <div
            key={pub.id}
            className="bg-white rounded-lg shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex flex-col"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {pub.title}
            </h2>
            <p className="text-md text-gray-700 mb-1">
              <strong>Authors:</strong> {pub.authors.join(", ")}
            </p>
            <p className="text-md text-gray-600 mb-1">
              <strong>Source:</strong> {pub.journalConference}
            </p>
            <p className="text-md text-gray-600 mb-2">
              <strong>Date:</strong>{" "}
              {new Date(pub.publicationDate).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
            <p className="text-gray-700 text-sm mb-4 flex-grow">
              {pub.abstract}
            </p>

            <div className="mt-auto flex flex-wrap gap-x-4 gap-y-2">
              {pub.link && (
                <a
                  href={pub.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  View Publication →
                </a>
              )}
              {pub.doi && (
                <a
                  href={`https://doi.org/${pub.doi}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 font-medium"
                >
                  DOI →
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <>
          <div className="flex justify-center mt-12 space-x-3">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                onClick={() => goToPage(index)}
                className={`w-10 h-10 rounded-full transition-all duration-200 flex items-center justify-center font-semibold text-sm ${
                  index === currentPage
                    ? "bg-blue-600 text-white scale-110 shadow-lg"
                    : "bg-gray-200 text-gray-600 hover:bg-gray-300 hover:scale-105"
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>

          {/* Slide counter */}
          <div className="text-center mt-4">
            <span className="text-sm text-gray-500">
              {currentPage + 1} of {totalPages} • Showing{" "}
              {Math.min(
                itemsPerPage,
                publications.length - currentPage * itemsPerPage
              )}{" "}
              of {publications.length} publications
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default PublicationList;
